import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * "Buy More, Save More" without pack variants: the chosen pack sets the quantity of
 * the base variant in this section's product form and refreshes the page price.
 * The automatic volume discount gives the pack price in cart and checkout.
 * A tier with a gift (data-gift-variant-id) adds the gift to the cart right before the
 * normal add to cart / pack checkout runs; the "Gift · NIDA Lip Mask" Function discount frees it.
 *
 * @extends Component
 */
class QuantityPackPicker extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  #giftPending = false;
  #passThrough = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.addEventListener('change', this.#apply, { signal: this.#abort.signal });
    // Window capture runs before the theme's document-capture on:submit / on:click handlers.
    window.addEventListener('submit', this.#beforeAdd, { capture: true, signal: this.#abort.signal });
    window.addEventListener('click', this.#beforeAdd, { capture: true, signal: this.#abort.signal });
    // Products with real variants (e.g. a mask type): the theme redraws price and quantity for the new
    // variant, so re-apply the chosen pack once that update lands.
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onVariant, { signal: this.#abort.signal });
    // Product form and price may upgrade after this element; apply once everything is defined.
    requestAnimationFrame(() => this.#apply());
    customElements.whenDefined('quantity-selector-component').then(() => this.#apply());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {Event} event */
  #onVariant = (event) => {
    const { promise } = /** @type {any} */ (event);
    (promise ?? Promise.resolve()).then(() => requestAnimationFrame(() => this.#apply())).catch(() => {});
  };

  get #selected() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'USD');
  }

  /** @param {Event} event */
  #beforeAdd = (event) => {
    if (this.#passThrough) return;
    const giftId = this.#selected?.dataset.giftVariantId;
    const section = this.closest('.shopify-section');
    if (!giftId || !(event.target instanceof Element) || !section?.contains(event.target)) return;

    /** @type {() => void} */
    let replay;
    if (event.type === 'submit') {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
      const { submitter } = /** @type {SubmitEvent} */ (event);
      replay = () => (submitter ? form.requestSubmit(submitter) : form.requestSubmit());
    } else {
      const button = event.target.closest('[data-pack-checkout]');
      if (!(button instanceof HTMLElement)) return;
      replay = () => button.click();
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#giftPending) return;
    this.#giftPending = true;
    this.#addGift(giftId).finally(() => {
      this.#giftPending = false;
      this.#passThrough = true;
      try {
        replay();
      } finally {
        this.#passThrough = false;
      }
    });
  };

  /**
   * Adds one gift unit unless the cart already has it (the discount frees only one unit).
   * Failures are ignored: the pack still goes to the cart.
   * @param {string} giftId
   */
  async #addGift(giftId) {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      if (cart.items?.some((item) => String(item.variant_id) === giftId)) return;
      await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(giftId), quantity: 1 }] }) }),
      });
    } catch {}
  }

  #apply = () => {
    const input = this.#selected;
    if (!input) return;
    const count = Number(input.value) || 1;
    const section = this.closest('.shopify-section') ?? document;

    // Base variant × count in every product form of the section (main + sticky).
    for (const form of section.querySelectorAll('form[action*="/cart/add"]:not([data-quantity-pack-exclude])')) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput instanceof HTMLInputElement && this.dataset.baseVariantId) idInput.value = this.dataset.baseVariantId;
    }
    for (const selector of section.querySelectorAll('quantity-selector-component')) {
      /** @type {any} */ (selector).setValue?.(String(count));
    }
    for (const quantity of section.querySelectorAll('product-form-component input[name="quantity"]')) {
      if (quantity.closest('form')?.hasAttribute('data-quantity-pack-exclude')) continue;
      if (quantity instanceof HTMLInputElement) quantity.value = String(count);
    }
    this.dispatchEvent(new QuantitySelectorUpdateEvent(count));

    this.#renderPrice(section, Number(input.dataset.price), Number(input.dataset.compare), Number(input.dataset.discount));
  };

  /**
   * @param {ParentNode} section
   * @param {number} price
   * @param {number} compare
   * @param {number} discount
   */
  #renderPrice(section, price, compare, discount) {
    const containers = section.querySelectorAll('product-price [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]');
    const sale = compare > price;
    const escapeText = (value) => value.replace(/[&<>]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[character]));
    const saleLabel = escapeText(this.dataset.saleLabel ?? 'Sale price');
    const regularLabel = escapeText(this.dataset.regularLabel ?? 'Regular price');
    // Keep the sticky purchase bar consistent with the selected quantity and total.
    for (const container of containers) {
      container.innerHTML = sale
      ? `<span class="price-item__group price"><span class="visually-hidden">${saleLabel}&nbsp;</span><bdi>${this.#money(price)}</bdi></span>` +
        `<span class="price-item__group price-item--regular compare-at-price"><span class="visually-hidden">${regularLabel}&nbsp;</span><bdi>${this.#money(compare)}</bdi></span>` +
        (discount > 0 ? `<span class="price-item__discount">−${discount}%</span>` : '')
      : `<span class="price"><bdi>${this.#money(price)}</bdi></span>`;
    }
  }
}

if (!customElements.get('quantity-pack-picker')) {
  customElements.define('quantity-pack-picker', QuantityPackPicker);
}
